import React, { useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { API, money } from "../lib/api";
import { useT } from "../lib/i18n";

const typeLabel = { phone: "Phone", email: "Email", tag: "Zelle Tag" };

const CopyButton = ({ value, label }) => {
  const { t } = useT();
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setDone(true);
      toast.success(t("copied"));
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error("Copy failed — please copy manually.");
    }
  };
  return (
    <button
      type="button" onClick={copy} aria-label={`${t("copy")} ${label}`}
      data-testid={`copy-${label}`}
      className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border bg-white text-[hsl(var(--muted-foreground))] transition-colors hover:text-[hsl(var(--brand-rose))]"
    >
      {done ? <Check className="h-4 w-4" style={{ color: "hsl(var(--success))" }} /> : <Copy className="h-4 w-4" />}
    </button>
  );
};

// Shared Zelle payment instructions with copy controls + optional QR code.
export default function ZellePayBlock({ zelle, amountCents, orderNumber }) {
  const { t } = useT();
  if (!zelle) return null;
  const recipient = zelle.recipient || zelle.handle || "";
  const rLabel = typeLabel[zelle.recipient_type] || "Recipient";

  return (
    <div className="rounded-xl border p-4" data-testid="zelle-pay-block">
      <div className="text-sm font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>{t("zelle_send")}</div>
      <div className="mt-1 text-sm">{t("amount")}: <b className="tabular-nums">{money(amountCents)}</b></div>
      {zelle.business_name && <div className="text-sm">{t("recipient")}: <b>{zelle.business_name}</b></div>}

      {recipient ? (
        <div className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-[hsl(var(--muted))] px-3 py-2">
          <div className="min-w-0">
            <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{rLabel}</div>
            <div className="truncate text-sm font-semibold" data-testid="zelle-recipient-value">{recipient}</div>
          </div>
          <CopyButton value={recipient} label="zelle-recipient" />
        </div>
      ) : (
        <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">Zelle recipient details will be provided shortly.</p>
      )}

      <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-[hsl(var(--muted))] px-3 py-2">
        <div className="min-w-0">
          <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{t("memo")}</div>
          <div className="truncate text-sm font-semibold" data-testid="zelle-memo-value">{orderNumber}</div>
        </div>
        <CopyButton value={orderNumber} label="order-number" />
      </div>

      <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">{zelle.instructions || t("zelle_memo_help")}</p>

      {zelle.has_qr && (
        <div className="mt-4 flex flex-col items-center gap-2 border-t pt-4" data-testid="zelle-qr">
          <div className="text-xs font-medium text-[hsl(var(--brand-brown))]">{t("scan_to_pay")}</div>
          <img src={`${API}/media/zelle-qr`} alt="Zelle QR code" className="h-40 w-40 rounded-lg border object-contain bg-white p-1" />
        </div>
      )}
    </div>
  );
}
